import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, useRef } from 'react';
import { Word, WordType } from '@goldcast/player-config/src/types/transcript-types';
import { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { getClipReadOnlyPlayerConfig } from '@goldcast/player-config/src/utils/clipUtils';
import { getSelectionDuration } from './utils';
import { TranscriptSelection } from './types';
import { TRANSCRIPT_VIRTUOSO_COMPONENT_ID } from './constants';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { getPlayerTimeFromSegmentTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';

export default function useTranscriptSelect({
  transcript,
  allDeletes
}: {
  transcript: Record<string, Word[]>;
  allDeletes: Record<string, ClipDeletes>;
}) {
  const {
    isSelecting,
    startTime: originalStartTime,
    endTime: originalEndTime,
    startSegmentId: originalStartSegmentId,
    endSegmentId: originalEndSegmentId,
    selectedDeleteIndex,
    selectedSegmentId
  } = useSyncExternalStore(transcriptTimelineSelectionStore.subscribe, transcriptTimelineSelectionStore.getSnapshot);

  const { clipData } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();
  const playerConfig = getClipReadOnlyPlayerConfig(clipData);
  // PlayerConfig will never be empty here.
  const segmentTrack = useMemo(() => getSegmentsTrack(playerConfig!), [playerConfig]);

  const startTime = useMemo(() => {
    if (selectedDeleteIndex !== null && !!selectedSegmentId) {
      const selectedSegmentDelete = allDeletes[selectedSegmentId];
      return selectedSegmentDelete[Object.keys(selectedSegmentDelete)[selectedDeleteIndex]].bounds[0];
    }
    return originalStartTime;
  }, [selectedDeleteIndex, originalStartTime, allDeletes, selectedSegmentId]);

  const endTime = useMemo(() => {
    if (selectedDeleteIndex !== null && !!selectedSegmentId) {
      const selectedSegmentDelete = allDeletes[selectedSegmentId];
      return selectedSegmentDelete[Object.keys(selectedSegmentDelete)[selectedDeleteIndex]].bounds[1];
    }
    return originalEndTime;
  }, [selectedDeleteIndex, originalEndTime, allDeletes, selectedSegmentId]);

  const startSegmentId = useMemo(() => {
    return selectedSegmentId ?? originalStartSegmentId;
  }, [selectedSegmentId, originalStartSegmentId]);

  const endSegmentId = useMemo(() => {
    return selectedSegmentId ?? originalEndSegmentId;
  }, [selectedSegmentId, originalEndSegmentId]);

  const [transcriptSelection, setTranscriptSelection] = useState<TranscriptSelection | null>();

  const autoScrollIntervalRef = useRef<{
    up: NodeJS.Timeout | null;
    down: NodeJS.Timeout | null;
  }>({ up: null, down: null });

  const cleanupAutoScrollInterval = useCallback(() => {
    if (autoScrollIntervalRef.current.up) {
      clearInterval(autoScrollIntervalRef.current.up);
      autoScrollIntervalRef.current.up = null;
    }
    if (autoScrollIntervalRef.current.down) {
      clearInterval(autoScrollIntervalRef.current.down);
      autoScrollIntervalRef.current.down = null;
    }
  }, []);

  const clearSelectionAndState = useCallback(() => {
    transcriptTimelineSelectionStore.set(() => ({ ...defaultTimelineSelectionState }));
    cleanupAutoScrollInterval();
  }, [cleanupAutoScrollInterval]);

  const getWordElementFromEvent = useCallback((e: Event): HTMLElement | null => {
    const target = e.target as HTMLElement;
    // Find the closest element with data-word-id attribute
    return target.closest('[data-word-id]') as HTMLElement;
  }, []);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      const virtuosoElement = document.getElementById(TRANSCRIPT_VIRTUOSO_COMPONENT_ID);
      if (!virtuosoElement) {
        return;
      }

      let startWordStartTime: string | null = null;
      let startWordEndTime: string | null = null;
      let startWordSegmentId: string | null = null;
      let endWordSegmentId: string | null = null;

      const setInitialSelection = (wordElement: HTMLElement) => {
        const wordId = wordElement.getAttribute('data-word-id');
        const newStartWordStartTime = wordElement.getAttribute('data-word-start-time');
        const newStartWordEndTime = wordElement.getAttribute('data-word-end-time');
        const newSegmentId = wordElement.getAttribute('data-segment-id');

        if (!wordId || newStartWordStartTime === null || newStartWordEndTime === null || newSegmentId === null) {
          return;
        }
        startWordStartTime = newStartWordStartTime;
        startWordEndTime = newStartWordEndTime;
        startWordSegmentId = newSegmentId;
        endWordSegmentId = newSegmentId;

        transcriptTimelineSelectionStore.update(() => ({
          ...defaultTimelineSelectionState,
          isSelecting: true,
          startTime: parseFloat(newStartWordStartTime),
          endTime: parseFloat(newStartWordEndTime),
          startSegmentId: startWordSegmentId,
          endSegmentId: endWordSegmentId
        }));
      };

      const wordElement = getWordElementFromEvent(e);
      if (wordElement) {
        e.preventDefault();
        setInitialSelection(wordElement);
      }

      const handleAutoScroll = (e: MouseEvent) => {
        const container = document.getElementById(TRANSCRIPT_VIRTUOSO_COMPONENT_ID);

        const { isSelecting } = transcriptTimelineSelectionStore.getSnapshot();

        if (!isSelecting || !container) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const mouseY = e.clientY;

        // Define scroll zones (within 50px of top/bottom)
        const scrollZone = 50;
        const scrollSpeed = 8;

        const distanceFromTop = mouseY - containerRect.top;
        const distanceFromBottom = containerRect.bottom - mouseY;
        const shouldMoveUp = distanceFromTop < scrollZone;
        const shouldMoveDown = distanceFromBottom < scrollZone;

        if (shouldMoveUp && !autoScrollIntervalRef.current.up) {
          // Near top - scroll up
          autoScrollIntervalRef.current.up = setInterval(() => {
            if (container && container.scrollTop > 0) {
              container.scrollBy({ top: -scrollSpeed, behavior: 'auto' });
            }
          }, 16); // ~60fps
        } else if (shouldMoveDown && !autoScrollIntervalRef.current.down) {
          // Near bottom - scroll down
          autoScrollIntervalRef.current.down = setInterval(() => {
            if (container && container.scrollTop < container.scrollHeight - container.clientHeight) {
              container.scrollBy({ top: scrollSpeed, behavior: 'auto' });
            }
          }, 16); // ~60fps
        } else if (!shouldMoveUp && !shouldMoveDown) {
          cleanupAutoScrollInterval();
        }
      };

      const onMouseEnter = (e: MouseEvent) => {
        // Handle auto-scrolling first
        handleAutoScroll(e);

        const wordElement = getWordElementFromEvent(e);
        if (!wordElement) {
          return;
        }

        if (startWordStartTime === null || startWordEndTime === null) {
          e.preventDefault();
          setInitialSelection(wordElement);
          return;
        }

        const currentWordStartTime = wordElement.getAttribute('data-word-start-time');
        const currentWordEndTime = wordElement.getAttribute('data-word-end-time');
        const currentSegmentId = wordElement.getAttribute('data-segment-id');

        if (!currentWordStartTime || !currentWordEndTime || !startWordStartTime || !currentSegmentId) {
          return;
        }

        const startSegmentIndex = segmentTrack.segments.findIndex(track => track.id === startWordSegmentId);
        const currentSegmentIndex = segmentTrack.segments.findIndex(segment => segment.id === currentSegmentId);
        const isSelectingInReverseDirection =
          currentSegmentIndex < startSegmentIndex ||
          (currentSegmentIndex === startSegmentIndex &&
            parseFloat(startWordStartTime) > parseFloat(currentWordStartTime));
        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          startTime: parseFloat(
            isSelectingInReverseDirection ? (currentWordStartTime ?? '0') : (startWordStartTime ?? '0')
          ),
          endTime: parseFloat(isSelectingInReverseDirection ? (startWordEndTime ?? '0') : (currentWordEndTime ?? '0')),
          startSegmentId: isSelectingInReverseDirection ? currentSegmentId : startWordSegmentId,
          endSegmentId: isSelectingInReverseDirection ? startWordSegmentId : currentSegmentId
        }));
      };

      const onMouseUp = () => {
        cleanupAutoScrollInterval();

        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          isSelecting: false
        }));

        const currentState = transcriptTimelineSelectionStore.getSnapshot();
        const { startTime, endTime, endSegmentId, startSegmentId } = currentState;

        const startSegmentDelete = startSegmentId ? allDeletes[startSegmentId] : null;
        const isWholeSelectionIsDeleted =
          startTime !== null &&
          endTime !== null &&
          startSegmentId !== null &&
          endSegmentId === startSegmentId &&
          startSegmentDelete &&
          !!Object.values(startSegmentDelete).find(
            deleteRange => deleteRange.bounds[0] <= startTime && deleteRange.bounds[1] >= endTime
          );

        if (
          startTime !== null &&
          endTime !== null &&
          startWordStartTime !== null &&
          startSegmentId !== null &&
          endSegmentId !== null &&
          !isWholeSelectionIsDeleted
        ) {
          const playerStartTime = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
          seekTo(playerStartTime, false);
        }
        document.removeEventListener('mousemove', handleAutoScroll);
        virtuosoElement.removeEventListener('mouseover', onMouseEnter);
        document.removeEventListener('pointerup', onMouseUp);
      };

      document.addEventListener('mousemove', handleAutoScroll);
      virtuosoElement.addEventListener('mouseover', onMouseEnter);
      document.addEventListener('pointerup', onMouseUp);
    },
    [getWordElementFromEvent, cleanupAutoScrollInterval, segmentTrack.segments, allDeletes, clipData, seekTo]
  );

  const onKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        clearSelectionAndState();
      }
    },
    [clearSelectionAndState]
  );

  const virtuosoElement = document.getElementById(TRANSCRIPT_VIRTUOSO_COMPONENT_ID);

  useEffect(() => {
    if (virtuosoElement) {
      virtuosoElement.addEventListener('mousedown', onMouseDown);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        virtuosoElement.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('keydown', onKeyDown);
        cleanupAutoScrollInterval();
      };
    }
  }, [virtuosoElement, onKeyDown, onMouseDown, cleanupAutoScrollInterval]);

  const {
    startIndex: nearestSelectionStartIndex,
    endIndex: nearestSelectionEndIndex,
    startSelectedSegmentId: nearestStartSelectedSegmentId,
    endSelectedSegmentId: nearestEndSelectedSegmentId
  } = useMemo(() => {
    if (startTime !== null && endTime !== null && startSegmentId !== null && endSegmentId !== null) {
      const startSegmentIndex = segmentTrack.segments.findIndex(segment => segment.id === startSegmentId);
      const endSegmentIndex = segmentTrack.segments.findIndex(segment => segment.id === endSegmentId);

      const nearestStartSegmentWithWords = segmentTrack.segments.find(
        (segment, index) => index >= startSegmentIndex && index <= endSegmentIndex && transcript[segment.id].length > 0
      );
      const nearestEndSegmentWithWords = segmentTrack.segments.find(
        (segment, index) => index >= endSegmentIndex && transcript[segment.id].length > 0
      );

      if (!nearestStartSegmentWithWords || !nearestEndSegmentWithWords) {
        return {
          startIndex: -1,
          endIndex: -1,
          startSelectedSegmentId: null,
          endSelectedSegmentId: null
        };
      }

      const nearestStartSegmentStartTime = nearestStartSegmentWithWords.id === startSegmentId ? startTime : 0;
      const nearestEndSegmentEndTime = nearestEndSegmentWithWords.id === endSegmentId ? endTime : 0;

      const shouldCheckForBoundary = startSegmentId === endSegmentId;

      const closestStart = transcript[nearestStartSegmentWithWords.id].reduce(
        (prev, { start_time: curr, end_time: currEnd, type, index }) =>
          Math.abs(curr - nearestStartSegmentStartTime) < Math.abs(prev.start_time - nearestStartSegmentStartTime) &&
          type !== WordType.Punctuation &&
          (!shouldCheckForBoundary ||
            ((curr + currEnd) / 2 >= nearestStartSegmentStartTime && (curr + currEnd) / 2 <= nearestEndSegmentEndTime))
            ? { start_time: curr, index }
            : prev,
        { start_time: Infinity, index: -1 }
      );

      const closestEnd = transcript[nearestEndSegmentWithWords.id].reduce(
        (prev, { start_time: curr, end_time: currEnd, type, index }) =>
          Math.abs(curr - nearestEndSegmentEndTime) < Math.abs(prev.start_time - nearestEndSegmentEndTime) &&
          type !== WordType.Punctuation &&
          (!shouldCheckForBoundary ||
            ((curr + currEnd) / 2 >= nearestStartSegmentStartTime && (curr + currEnd) / 2 <= nearestEndSegmentEndTime))
            ? { start_time: curr, index }
            : prev,
        { start_time: Infinity, index: -1 }
      );

      return {
        startIndex: closestStart.index,
        endIndex: closestEnd.index,
        startSelectedSegmentId: nearestStartSegmentWithWords.id,
        endSelectedSegmentId: nearestEndSegmentWithWords.id
      };
    }

    return {
      startIndex: -1,
      endIndex: -1,
      startSelectedSegmentId: null,
      endSelectedSegmentId: null
    };
  }, [startTime, endTime, startSegmentId, endSegmentId, segmentTrack.segments, transcript]);

  useEffect(() => {
    if (!isSelecting) {
      if (
        nearestSelectionStartIndex === -1 ||
        nearestSelectionEndIndex === -1 ||
        nearestStartSelectedSegmentId === null ||
        nearestEndSelectedSegmentId === null
      ) {
        setTranscriptSelection(null);
      } else {
        const nearestSelectionStartSegmentIndex = segmentTrack.segments.findIndex(
          segment => segment.id === nearestStartSelectedSegmentId
        );
        const nearestSelectionEndSegmentIndex = segmentTrack.segments.findIndex(
          segment => segment.id === nearestEndSelectedSegmentId
        );
        const selectedWords: Record<string, Word[]> = {};
        for (let i = nearestSelectionStartSegmentIndex; i <= nearestSelectionEndSegmentIndex; i++) {
          if (transcript[segmentTrack.segments[i].id].length === 0) {
            selectedWords[segmentTrack.segments[i].id] = [];
            continue;
          }
          selectedWords[segmentTrack.segments[i].id] = transcript[segmentTrack.segments[i].id].slice(
            i === nearestSelectionStartSegmentIndex ? nearestSelectionStartIndex : 0,
            i === nearestSelectionEndSegmentIndex
              ? nearestSelectionEndIndex + 1
              : transcript[segmentTrack.segments[i].id].length
          );
        }

        const firstWord = transcript[nearestStartSelectedSegmentId][nearestSelectionStartIndex];
        const lastWord = transcript[nearestEndSelectedSegmentId][nearestSelectionEndIndex];
        const isSingleParagraphSelection =
          nearestStartSelectedSegmentId === nearestEndSelectedSegmentId && firstWord?.srt_index === lastWord?.srt_index;
        setTranscriptSelection({
          duration: getSelectionDuration(selectedWords),
          words: selectedWords,
          isSingleParagraphSelection: isSingleParagraphSelection,
          selectionStartTime: firstWord.start_time,
          selectionEndTime: lastWord.end_time,
          startSegmentId,
          endSegmentId
        });
      }
    }
  }, [
    nearestSelectionStartIndex,
    nearestSelectionEndIndex,
    nearestStartSelectedSegmentId,
    nearestEndSelectedSegmentId,
    isSelecting,
    transcript,
    segmentTrack.segments,
    startSegmentId,
    endSegmentId
  ]);

  const nearestStartSelectedSegmentIndex = useMemo(() => {
    return segmentTrack.segments.findIndex(segment => segment.id === nearestStartSelectedSegmentId);
  }, [nearestStartSelectedSegmentId, segmentTrack.segments]);

  const nearestEndSelectedSegmentIndex = useMemo(() => {
    return segmentTrack.segments.findIndex(segment => segment.id === nearestEndSelectedSegmentId);
  }, [nearestEndSelectedSegmentId, segmentTrack.segments]);

  return {
    onCleanupSelection: clearSelectionAndState,
    startTime,
    endTime,
    startSegmentId,
    endSegmentId,
    nearestSelectionStartIndex,
    nearestSelectionEndIndex,
    nearestStartSelectedSegmentId,
    nearestEndSelectedSegmentId,
    nearestStartSelectedSegmentIndex,
    nearestEndSelectedSegmentIndex,
    isSelecting,
    transcriptSelection
  };
}
